// ─── Courses d'un ou plusieurs MENUS — module PUR ─────────────────
// « Qu'est-ce que je dois acheter pour ces semaines ? » : on parcourt les cases
// des menus retenus et on additionne les ingrédients, regroupés par nom+unité.
//
// Le nombre de personnes est porté par CHAQUE case (pas de réglage global) :
// un dîner pour 2 et un déjeuner pour 6 dans le même menu donnent bien des
// quantités différentes. On réutilise les mises à l'échelle de `courses.js`
// plutôt que de refaire le calcul.
// Extension `.js` explicite : Metro s'en passe, Node (donc `npm test`) l'exige.
import { ingredientsRecetteEchelle, ingredientsRepasEchelle } from './courses.js';
import { personnesRecette, personnesRepas, personnesCase } from './personnes.js';

const arrondir = (n) => Math.round(n * 100) / 100;
const nombre = (q) => {
  const m = String(q || '').replace(',', '.').match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : NaN;
};

// Ingrédients d'UNE case (repas, recette seule, ou texte libre → rien à acheter).
function ingredientsCase(c, recettesById, repasById) {
  if (c?.repasId) {
    const rp = repasById?.[c.repasId];
    if (!rp) return [];
    return ingredientsRepasEchelle(rp, recettesById, personnesCase(c, rp) || personnesRepas(rp));
  }
  if (c?.recetteId) {
    const rec = recettesById?.[c.recetteId];
    if (!rec) return [];
    return ingredientsRecetteEchelle(rec, Number(c.personnes) || personnesRecette(rec));
  }
  return [];
}

// Agrège plusieurs menus en une liste de courses.
// Regroupement par nom + unité : « 200 g farine » et « 300 g farine » font une
// ligne ; « 2 œufs » et « 200 g œufs » restent séparés, l'unité n'étant pas
// convertible. Une quantité non numérique (« au goût ») ne s'additionne pas :
// on garde les mentions telles quelles, côte à côte.
export function coursesDesMenus(menus, recettesById = {}, repasById = {}) {
  const sommes = new Map();     // 'nom|unite' → { nom, unite, total }
  const textes = new Map();     // 'nom|unite' → { nom, unite, mentions[] }

  for (const menu of (menus || [])) {
    for (const cle of Object.keys(menu?.cases || {})) {
      for (const ing of ingredientsCase(menu.cases[cle], recettesById, repasById)) {
        const k = `${(ing.nom || '').toLowerCase()}|${(ing.unite || '').toLowerCase()}`;
        const n = nombre(ing.quantite);
        if (isNaN(n)) {
          const e = textes.get(k) || { nom: ing.nom, unite: ing.unite, mentions: [] };
          if (ing.quantite) e.mentions.push(String(ing.quantite));
          textes.set(k, e);
        } else {
          const e = sommes.get(k) || { nom: ing.nom, unite: ing.unite, total: 0 };
          e.total += n;
          sommes.set(k, e);
        }
      }
    }
  }

  const lignes = [
    ...[...sommes.values()].map(e => ({ nom: e.nom, unite: e.unite, quantite: String(arrondir(e.total)) })),
    ...[...textes.values()].map(e => ({ nom: e.nom, unite: e.unite, quantite: e.mentions.join(', ') })),
  ];
  lignes.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
  return lignes;
}

// Menus des semaines retenues, dans l'ordre des semaines. Une semaine sans menu
// affecté est ignorée — silencieusement, c'est le cas normal.
export function menusDesSemaines(lundis, semaines, menus) {
  const parLundi = Object.fromEntries((semaines || []).map(s => [s.lundi, s]));
  const out = [];
  for (const lundi of [...(lundis || [])].sort()) {
    const m = (menus || []).find(x => x.id === parLundi[lundi]?.menuId);
    if (m) out.push(m);
  }
  return out;
}
